import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/elements/button';
import FormInput from '../../components/elements/form_input';
import { Send, Download, Print, Phone, Back, Upload, Check } from '../../assets/icons';
import '../../assets/css/lab/case.css';
import { casesApi } from '../../network/apis/cases';
import { messageApi } from '../../network/apis/message';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const LabCase = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);
    
    const [activeTab, setActiveTab] = useState('details');
    const [message, setMessage] = useState('');
    const [caseData, setCaseData] = useState(location.state?.case || null);
    const [loading, setLoading] = useState(!location.state?.case);
    const [error, setError] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    
    // Lab result form state
    const [showResultForm, setShowResultForm] = useState(false);
    const [selectedSample, setSelectedSample] = useState(null);
    const [resultData, setResultData] = useState({});

    useEffect(() => {
        if (!caseData && id) {
            fetchCaseDetails();
        }
        fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchCaseDetails = async () => {
        try {
            setLoading(true);
            const response = await casesApi.getCase(id);
            if (response.data.data) {
                setCaseData(response.data.data);
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch case details');
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        if (!caseData?.case_code && !id) return;
        
        setIsLoadingMessages(true);
        try {
            const chatId = caseData?.case_code ? `case_${caseData.case_code}` : `case_${id}`;
            const response = await messageApi.getMessages({ chat_id: chatId });
            if (response.data.data) {
                setMessages(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const handleMessageSend = async () => {
        if (!message.trim() || isSendingMessage) return;
        
        setIsSendingMessage(true);
        try {
            const chatId = caseData?.case_code ? `case_${caseData.case_code}` : `case_${id}`;
            const response = await messageApi.newMessage({
                chat_id: chatId,
                content: message,
                attachments: []
            });
            if (response.data.data) {
                setMessages(prev => [...prev, response.data.data]);
            }
            setMessage('');
        } catch (err) {
            console.error('Error sending message:', err);
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleMessageSend();
        }
    };

    const handleAddResult = (sample) => {
        setSelectedSample(sample);
        setShowResultForm(true);
        // Initialize result data with sample parameters
        const initialData = {};
        sample.parameters?.forEach(param => {
            initialData[param] = '';
        });
        setResultData(initialData);
    };

    const handleSubmitResult = async () => {
        // TODO: Submit results to API
        console.log('Submitting results for sample:', selectedSample?.id, resultData);
        setShowResultForm(false);
        setSelectedSample(null);
        setResultData({});
    };

    const getStatusColor = (status) => {
        const colors = {
            'pending': 'pending',
            'open': 'pending',
            'in-progress': 'processing',
            'processing': 'processing',
            'completed': 'completed',
            'resolved': 'completed'
        };
        return colors[status?.toLowerCase()] || 'pending';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'high': 'high',
            'medium': 'medium',
            'low': 'low'
        };
        return colors[priority?.toLowerCase()] || 'medium';
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Something went wrong</h2>
                <p>{error}</p>
                <Button text="Go Back" variant="outline" onClick={() => navigate(-1)} />
            </div>
        );
    }

    if (!caseData) {
        return (
            <div className="error-container">
                <h2>Case Not Found</h2>
                <p>The case you&apos;re looking for doesn&apos;t exist or has been removed.</p>
                <Button text="Go Back" variant="outline" onClick={() => navigate('/lab/cases')} />
            </div>
        );
    }

    return (
        <div className="lab-case-details">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <button className="back-btn" onClick={() => navigate('/lab/cases')}>
                    <img src={Back} alt="" />
                    <span>Back to Cases</span>
                </button>
            </div>

            {/* Case Header */}
            <div className="case-header">
                <div className="header-content">
                    <div className="title-section">
                        <h1>{caseData.title || `Case #${caseData.id}`}</h1>
                        <span className={`status-badge ${getStatusColor(caseData.status)}`}>
                            {caseData.status}
                        </span>
                        <span className={`priority-badge ${getPriorityColor(caseData.priority)}`}>
                            {caseData.priority} Priority
                        </span>
                    </div>
                    <p className="case-subtitle">
                        {caseData.farm?.name || caseData.farmName || 'Unknown Farm'} 
                        {(caseData.farm?.location || caseData.farmLocation) && 
                            ` • ${caseData.farm?.location || caseData.farmLocation}`
                        }
                    </p>
                </div>
                
                <div className="header-actions">
                    <Button
                        icon={<img src={Print} alt="" />}
                        text="Print Report"
                        variant="outline"
                    />
                    <Button
                        icon={<img src={Download} alt="" />}
                        text="Export"
                        variant="outline"
                    />
                    <Button
                        text="Update Status"
                        variant="primary"
                    />
                </div>
            </div>

            {/* Case Content */}
            <div className="case-content">
                <nav className="case-tabs">
                    <button 
                        className={activeTab === 'details' ? 'active' : ''}
                        onClick={() => setActiveTab('details')}
                    >
                        <span>Case Details</span>
                    </button>
                    <button 
                        className={activeTab === 'samples' ? 'active' : ''}
                        onClick={() => setActiveTab('samples')}
                    >
                        <span>Samples & Results</span>
                        {caseData.samples?.filter(s => s.status === 'pending').length > 0 && (
                            <span className="tab-badge">
                                {caseData.samples.filter(s => s.status === 'pending').length}
                            </span>
                        )}
                    </button>
                    <button 
                        className={activeTab === 'communication' ? 'active' : ''}
                        onClick={() => setActiveTab('communication')}
                    >
                        <span>Communication</span>
                    </button>
                    <button 
                        className={activeTab === 'timeline' ? 'active' : ''}
                        onClick={() => setActiveTab('timeline')}
                    >
                        <span>Activity</span>
                    </button>
                </nav>

                <div className="tab-content">
                    {activeTab === 'details' && (
                        <div className="details-tab">
                            <section className="case-overview">
                                <h2>Case Overview</h2>
                                <div className="overview-grid">
                                    <div className="info-card">
                                        <label>Case ID</label>
                                        <span className="value">#{caseData.id || caseData.case_code}</span>
                                    </div>
                                    <div className="info-card">
                                        <label>Test Type</label>
                                        <span className="value">{caseData.testType || caseData.type || 'General Analysis'}</span>
                                    </div>
                                    <div className="info-card">
                                        <label>Created</label>
                                        <span className="value">
                                            {caseData.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'N/A'}
                                        </span>
                                    </div>
                                    <div className="info-card">
                                        <label>Target Date</label>
                                        <span className="value">{caseData.targetDate || caseData.target_date || 'Not set'}</span>
                                    </div>
                                </div>

                                <div className="description-card">
                                    <h3>Description</h3>
                                    <p>{caseData.description || 'No description provided.'}</p>
                                </div>

                                {caseData.symptoms && caseData.symptoms.length > 0 && (
                                    <div className="symptoms-card">
                                        <h3>Reported Symptoms</h3>
                                        <div className="symptoms-list">
                                            {caseData.symptoms.map((symptom, index) => (
                                                <span key={index} className="symptom-tag">{symptom}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </section>

                            <section className="contact-section">
                                <h2>Contact Information</h2>
                                <div className="contact-grid">
                                    <div className="contact-card">
                                        <div className="contact-avatar">
                                            {(caseData.contact?.name || caseData.farmer?.name || 'F').charAt(0)}
                                        </div>
                                        <div className="contact-info">
                                            <h3>{caseData.contact?.name || caseData.farmer?.name || 'Farm Owner'}</h3>
                                            <p>{caseData.contact?.phone || caseData.farmer?.phone || 'No phone'}</p>
                                            <p>{caseData.contact?.email || caseData.farmer?.email || 'No email'}</p>
                                        </div>
                                        <Button
                                            icon={<img src={Phone} alt="" />}
                                            text="Call"
                                            variant="outline"
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </section>

                            {caseData.doctor && (
                                <section className="assigned-section">
                                    <h2>Assigned Doctor</h2>
                                    <div className="doctor-card">
                                        <div className="doctor-avatar">
                                            {caseData.doctor.name?.charAt(0) || 'D'}
                                        </div>
                                        <div className="doctor-info">
                                            <h3>{caseData.doctor.name}</h3>
                                            <p>{caseData.doctor.specialty || 'Aquaculture Specialist'}</p>
                                        </div>
                                    </div>
                                </section>
                            )}
                        </div>
                    )}

                    {activeTab === 'samples' && (
                        <div className="samples-tab">
                            <div className="samples-header">
                                <h2>Samples & Lab Results</h2>
                                <Button
                                    icon={<img src={Upload} alt="" />}
                                    text="Upload Results"
                                    variant="primary"
                                />
                            </div>

                            {caseData.samples && caseData.samples.length > 0 ? (
                                <div className="samples-list">
                                    {caseData.samples.map(sample => (
                                        <div key={sample.id} className="sample-card">
                                            <div className="sample-header">
                                                <div className="sample-title">
                                                    <h3>{sample.type} Sample</h3>
                                                    <span className="sample-id">{sample.id}</span>
                                                </div>
                                                <span className={`status-badge ${getStatusColor(sample.status)}`}>
                                                    {sample.status}
                                                </span>
                                            </div>
                                            
                                            <div className="sample-meta">
                                                <div className="meta-item">
                                                    <label>Collection Date</label>
                                                    <span>{sample.collectionDate || sample.collection_date || 'N/A'}</span>
                                                </div>
                                                <div className="meta-item">
                                                    <label>Parameters</label>
                                                    <div className="parameters">
                                                        {sample.parameters?.map(param => (
                                                            <span key={param} className="parameter-tag">
                                                                {param}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {sample.results ? (
                                                <div className="results-section">
                                                    <h4>Results</h4>
                                                    <table className="results-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Parameter</th>
                                                                <th>Value</th>
                                                                <th>Reference Range</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Object.entries(sample.results).map(([param, value]) => (
                                                                <tr key={param}>
                                                                    <td>{param}</td>
                                                                    <td className="value">{value}</td>
                                                                    <td className="reference">-</td>
                                                                    <td>
                                                                        <span className="result-status normal">Normal</span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="no-results">
                                                    <p>No results submitted yet</p>
                                                    <Button
                                                        icon={<img src={Check} alt="" />}
                                                        text="Add Results"
                                                        variant="primary"
                                                        onClick={() => handleAddResult(sample)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-samples">
                                    <p>No samples have been collected for this case yet.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'communication' && (
                        <div className="communication-tab">
                            <div className="messages-container">
                                {isLoadingMessages ? (
                                    <div className="loading-messages">
                                        <LoadingSpinner />
                                    </div>
                                ) : messages.length > 0 ? (
                                    messages.map((msg, index) => (
                                        <div 
                                            key={msg.id || index} 
                                            className={`message ${msg.sender === 'lab' ? 'sent' : 'received'}`}
                                        >
                                            <div className="message-header">
                                                <div className="sender-info">
                                                    <div className="sender-avatar">
                                                        {(msg.senderName || msg.sender || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <h4>{msg.senderName || msg.sender_name || 'User'}</h4>
                                                        <span className="role">{msg.role || 'Lab Technician'}</span>
                                                    </div>
                                                </div>
                                                <span className="timestamp">
                                                    {msg.time || (msg.created_at && new Date(msg.created_at).toLocaleTimeString())}
                                                </span>
                                            </div>
                                            
                                            <div className="message-content" style={{ whiteSpace: 'pre-wrap' }}>
                                                <p>{msg.content}</p>
                                            </div>

                                            {msg.attachments && msg.attachments.length > 0 && (
                                                <div className="attachments">
                                                    {msg.attachments.map((file, idx) => (
                                                        <div key={idx} className="attachment">
                                                            <span className="file-name">{file.name}</span>
                                                            <span className="file-size">{file.size}</span>
                                                            <button className="download-btn">
                                                                <img src={Download} alt="Download" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-messages">
                                        <p>No messages yet. Start the conversation!</p>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className="message-input">
                                <div className="input-wrapper">
                                    <FormInput
                                        type="textarea"
                                        placeholder="Type your message..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                                <div className="input-actions">
                                    <div className="left-actions">
                                        <input
                                            type="file"
                                            id="file-upload"
                                            className="hidden"
                                            multiple
                                        />
                                        <label htmlFor="file-upload" className="attach-btn">
                                            <img src={Upload} alt="" />
                                            <span>Attach</span>
                                        </label>
                                    </div>
                                    <Button
                                        icon={<img src={Send} alt="" />}
                                        text={isSendingMessage ? "Sending..." : "Send"}
                                        variant="primary"
                                        onClick={handleMessageSend}
                                        disabled={isSendingMessage || !message.trim()}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'timeline' && (
                        <div className="timeline-tab">
                            <h2>Case Activity</h2>
                            <div className="timeline">
                                {caseData.timeline && caseData.timeline.length > 0 ? (
                                    caseData.timeline.map((event, index) => (
                                        <div key={index} className="timeline-item">
                                            <div className="timeline-marker"></div>
                                            <div className="timeline-content">
                                                <span className="timeline-date">
                                                    {new Date(event.date || event.created_at).toLocaleDateString()}
                                                </span>
                                                <p>{event.content || event.description}</p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="timeline-item">
                                        <div className="timeline-marker"></div>
                                        <div className="timeline-content">
                                            <span className="timeline-date">
                                                {caseData.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'Today'}
                                            </span>
                                            <p>Case created</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Result Form Modal */}
            {showResultForm && selectedSample && (
                <div className="modal-overlay" onClick={() => setShowResultForm(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add Results for {selectedSample.type} Sample</h2>
                            <button className="close-btn" onClick={() => setShowResultForm(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p className="sample-info">Sample ID: {selectedSample.id}</p>
                            {selectedSample.parameters?.map(param => (
                                <div key={param} className="result-input">
                                    <label>{param}</label>
                                    <input
                                        type="text"
                                        value={resultData[param] || ''}
                                        onChange={(e) => setResultData({
                                            ...resultData,
                                            [param]: e.target.value
                                        })}
                                        placeholder={`Enter ${param} value`}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="modal-footer">
                            <Button text="Cancel" variant="outline" onClick={() => setShowResultForm(false)} />
                            <Button text="Submit Results" variant="primary" onClick={handleSubmitResult} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LabCase;